import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Trash2, Phone, Mail, MessageCircle, Download } from "lucide-react";

const STATUSES = ["New", "Contacted", "In Discussion", "Converted", "Closed"];

const STATUS_CLS = {
  New: "border-[#FF8800]/40 text-[#FF8800]",
  Contacted: "border-[#00B0FF]/40 text-[#00B0FF]",
  "In Discussion": "border-[#00E676]/40 text-[#00E676]",
  Converted: "border-emerald-400/50 text-emerald-300",
  Closed: "border-slate-500/40 text-slate-500",
};

export function EnquiriesModule() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [notes, setNotes] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/enquiries")
      .then((r) => {
        setItems(r.data);
        const n = {};
        r.data.forEach((i) => (n[i.id] = i.notes || ""));
        setNotes(n);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((i) => {
      const matchStatus = statusFilter === "All" || i.status === statusFilter;
      const matchQ = !q || [i.name, i.phone, i.email, i.company, i.requirement].join(" ").toLowerCase().includes(q);
      return matchStatus && matchQ;
    });
  }, [items, query, statusFilter]);

  const updateItem = async (item, patch, successMsg) => {
    try {
      await api.put(`/admin/enquiries/${item.id}`, { ...item, ...patch });
      if (successMsg) toast.success(successMsg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete enquiry from "${item.name}"?`)) return;
    try {
      await api.delete(`/admin/enquiries/${item.id}`);
      toast.success("Enquiry deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportCsv = () => {
    const header = ["Name", "Phone", "Email", "Company", "Requirement", "Message", "Preferred Contact", "Source", "Status", "Notes", "Date"];
    const rows = filtered.map((i) =>
      [i.name, i.phone, i.email, i.company, i.requirement, i.message, i.preferred_contact, i.source, i.status, i.notes, i.created_at]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [header.join(","), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `launchlab-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-module-enquiries">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">Contact Enquiries</h2>
          <p className="text-sm text-slate-400">{items.length} total · {items.filter((i) => i.status === "New").length} new</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input data-testid="enquiries-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..." className="pl-9 bg-[#0D111D] border-white/10 w-44" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="enquiries-filter" className="bg-[#0D111D] border-white/10 text-slate-200 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
              {["All", ...STATUSES].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={exportCsv} variant="outline" data-testid="enquiries-export" className="border-white/15 text-slate-300 hover:bg-white/5">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center text-slate-400" data-testid="enquiries-empty">No enquiries found.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => (
            <div key={item.id} data-testid={`enquiry-row-${item.id}`} className="glass rounded-xl p-5">
              <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <p className="font-display font-semibold text-white">{item.name}</p>
                    <span className={`text-[10px] font-mono-tech px-2 py-0.5 rounded-full border ${STATUS_CLS[item.status] || STATUS_CLS.New}`}>
                      {(item.status || "New").toUpperCase()}
                    </span>
                    <span className="text-[10px] font-mono-tech px-2 py-0.5 rounded-full border border-[#00B0FF]/40 text-[#00B0FF]">
                      {item.requirement}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300 mt-2 leading-relaxed">{item.message}</p>
                  <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-400">
                    <a href={`tel:${(item.phone || "").replace(/[^+\d]/g, "")}`} data-testid={`enquiry-call-${item.id}`} className="flex items-center gap-1.5 hover:text-[#00E676] transition-colors">
                      <Phone className="w-3.5 h-3.5" /> {item.phone}
                    </a>
                    <a href={`https://wa.me/${(item.phone || "").replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid={`enquiry-whatsapp-${item.id}`} className="flex items-center gap-1.5 hover:text-[#00E676] transition-colors">
                      <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                    <a href={`mailto:${item.email}`} data-testid={`enquiry-email-${item.id}`} className="flex items-center gap-1.5 hover:text-[#00B0FF] transition-colors">
                      <Mail className="w-3.5 h-3.5" /> {item.email}
                    </a>
                    {item.company && <span className="text-slate-500">· {item.company}</span>}
                    {item.preferred_contact && <span className="text-slate-500">· Prefers {item.preferred_contact}</span>}
                    {item.source && <span className="text-slate-500">· via {item.source}</span>}
                  </div>
                </div>
                <div className="flex lg:flex-col items-center gap-2 shrink-0">
                  <Select value={item.status || "New"} onValueChange={(v) => updateItem(item, { status: v }, "Status updated")}>
                    <SelectTrigger data-testid={`enquiry-status-${item.id}`} className="bg-[#06080E] border-white/10 text-slate-200 w-36 h-9 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="icon" variant="outline" data-testid={`enquiry-delete-${item.id}`} onClick={() => remove(item)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Textarea
                  data-testid={`enquiry-notes-${item.id}`}
                  rows={1}
                  value={notes[item.id] ?? ""}
                  onChange={(e) => setNotes((n) => ({ ...n, [item.id]: e.target.value }))}
                  placeholder="Private notes..."
                  className="bg-[#06080E] border-white/10 text-xs min-h-[38px]"
                />
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`enquiry-notes-save-${item.id}`}
                  onClick={() => updateItem(item, { notes: notes[item.id] ?? "" }, "Notes saved")}
                  className="border-white/15 text-slate-300 hover:bg-white/5 shrink-0"
                >
                  Save Note
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
